import {
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  subWeeks,
  subMonths,
  getWeek,
  setWeek,
  startOfYear,
  format,
  isBefore,
  isThisWeek,
} from 'date-fns'

import { WeekNumber, DateRange } from './types'
import { Suffix } from './types'

// Helper function to get current week number (1-52)
export const getCurrentWeekNumber = (): WeekNumber => {
  const now = new Date()
  return getWeek(now, { firstWeekContainsDate: 1 })
}

// Helper function to calculate date range for Last prefix
export const calculateLastRange = (suffix: Suffix): DateRange => {
  const now = new Date()

  switch (suffix) {
    case Suffix.Week:
      const lastWeekStart = startOfWeek(subWeeks(now, 1), { weekStartsOn: 1 })
      const lastWeekEnd = endOfWeek(subWeeks(now, 1), { weekStartsOn: 1 })
      return {
        start: lastWeekStart.toISOString(),
        end: lastWeekEnd.toISOString(),
      }
    case Suffix.Month:
      const lastMonthStart = startOfMonth(subMonths(now, 1))
      const lastMonthEnd = endOfMonth(subMonths(now, 1))
      return {
        start: lastMonthStart.toISOString(),
        end: lastMonthEnd.toISOString(),
      }
    default:
      return {
        start: subWeeks(now, 1).toISOString(),
        end: now.toISOString(),
      }
  }
}

// Helper function to calculate date range for Week prefix
export const calculateWeekRange = (weekNumber: WeekNumber): DateRange => {
  const currentYear = new Date().getFullYear()
  const firstDayOfYear = startOfYear(new Date(currentYear, 0, 1))

  // Set to the specified week
  const weekStart = startOfWeek(
    setWeek(firstDayOfYear, weekNumber, { firstWeekContainsDate: 1 }),
    { weekStartsOn: 1 },
  )
  const weekEnd = endOfWeek(weekStart, { weekStartsOn: 1 })

  return {
    start: weekStart.toISOString(),
    end: weekEnd.toISOString(),
  }
}

// Format a week range for display (e.g., "Jan 1 - Jan 7")
export const formatWeekRange = (weekNumber: WeekNumber): string => {
  const { start, end } = calculateWeekRange(weekNumber)
  const startDate = new Date(start)
  const endDate = new Date(end)

  return `${format(startDate, 'MMM d')} - ${format(endDate, 'MMM d')}`
}

// Get all past weeks for the current year (including current week)
export const getPastWeeks = (): { value: WeekNumber; label: string }[] => {
  const currentYear = new Date().getFullYear()
  const currentWeek = getCurrentWeekNumber()
  const weeks = []

  // Loop through all weeks up to the current one
  for (let i = 1; i <= currentWeek; i++) {
    const weekStart = startOfWeek(
      setWeek(new Date(currentYear, 0, 1), i, { firstWeekContainsDate: 1 }),
      { weekStartsOn: 1 },
    )

    // Only include weeks that have already started
    if (
      isBefore(weekStart, new Date()) ||
      isThisWeek(weekStart, { weekStartsOn: 1 })
    ) {
      weeks.push({
        value: i,
        label: formatWeekRange(i),
      })
    }
  }

  // Return in reverse order (most recent first)
  return weeks.reverse()
}
